import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/components/ui/use-toast'
import * as profilesApi from '../api/profiles'
import type { SocialProfile } from '../types'

/**
 * Fetch all social profiles
 */
export function useProfiles() {
    return useQuery({
        queryKey: ['social-profiles'],
        queryFn: () => profilesApi.getProfiles()
    })
}

/**
 * Create a new social profile
 */
export function useCreateProfile() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (profile: Partial<SocialProfile>) => profilesApi.createProfile(profile),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['social-profiles'] })
            toast({
                title: "✅ Profile created",
                description: "Social media profile has been added successfully.",
                duration: 3000,
            })
        },
        onError: (error: any) => {
            toast({
                title: "❌ Failed to create profile",
                description: error.message || "Unable to create profile. Please try again.",
                variant: "destructive",
                duration: 0,
            })
        }
    })
}

/**
 * Update an existing social profile
 */
export function useUpdateProfile() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: ({ id, ...updates }: Partial<SocialProfile> & { id: string }) =>
            profilesApi.updateProfile(id, updates),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['social-profiles'] })
            toast({
                title: "✅ Profile updated",
                description: "Changes saved successfully.",
                duration: 3000,
            })
        },
        onError: (error: any) => {
            toast({
                title: "❌ Failed to update profile",
                description: error.message || "Unable to save changes. Please try again.",
                variant: "destructive",
                duration: 0,
            })
        }
    })
}

/**
 * Delete a social profile
 */
export function useDeleteProfile() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (id: string) => profilesApi.deleteProfile(id),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['social-profiles'] })
            toast({
                title: "✅ Profile deleted",
                description: "The profile has been removed.",
                duration: 3000,
            })
        },
        onError: (error: any) => {
            toast({
                title: "❌ Failed to delete profile",
                description: error.message || "Unable to delete profile. Please try again.",
                variant: "destructive",
                duration: 0,
            })
        }
    })
}
